"use client";

import { useEffect, useRef } from "react";
import { subscribeToFlutterMessage } from "@/utils/flutterBridge";
import { FlutterIncomingMessageType } from "@/utils/flutterBridge/flutterBridge.types";

const DEBOUNCE_MS = 100;

/**
 * Hook to control video playback based on Flutter messages.
 * Pauses video when VIDEO_PAUSE message is received (from Flutter or internal dispatch).
 * Resumes video when VIDEO_RESUME message is received.
 * Restarts video from beginning when VIDEO_RESTART message is received.
 * Debounces rapid messages to prevent video flicker.
 *
 * @param videoRef - Reference to the video element
 * @param enabled - Whether to enable this behavior (only for webview)
 */
export const useFlutterVideoControl = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean = false,
) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track pending action (what we're about to do) separately from executed action
  const pendingActionRef = useRef<"pause" | "resume" | "restart" | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribe = subscribeToFlutterMessage((message) => {
      const video = videoRef.current;
      if (!video) return;

      const action =
        message.type === FlutterIncomingMessageType.VIDEO_PAUSE
          ? "pause"
          : message.type === FlutterIncomingMessageType.VIDEO_RESUME
            ? "resume"
            : message.type === FlutterIncomingMessageType.VIDEO_RESTART
              ? "restart"
              : null;

      if (!action) return;

      // Skip if same action is already pending
      if (action === pendingActionRef.current) return;

      // Clear any pending debounced action
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Mark this action as pending immediately
      pendingActionRef.current = action;

      // Debounce the execution
      debounceTimerRef.current = setTimeout(() => {
        if (action === "pause") {
          video.pause();
        } else if (action === "restart") {
          video.currentTime = 0;
          video.play().catch(() => {
            // Autoplay might be blocked, ignore the error
          });
        } else {
          video.play().catch(() => {
            // Autoplay might be blocked, ignore the error
          });
        }
        debounceTimerRef.current = null;
        pendingActionRef.current = null;
      }, DEBOUNCE_MS);
    });

    return () => {
      unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enabled, videoRef]);
};
